<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <div class="breadcrumb-spacer"></div>
    <h1 class="page-title">User Feedback</h1>
  </div>

  <div class="feedback-container">
    <!-- Filters Section -->
    <mat-card class="filters-card">
      <mat-card-content>
        <form [formGroup]="filterForm" class="filters-form">
          <div class="filter-row">
            <!-- Search Field -->
            <mat-form-field appearance="outline" class="search-field">
              <mat-label
                >Search by message, user name, email, page URL...</mat-label
              >
              <input
                matInput
                [(ngModel)]="searchQuery"
                [ngModelOptions]="{ standalone: true }"
                placeholder="Search by message, user name, email, page URL..."
                autocomplete="off"
                maxlength="200"
                (input)="onSearchInput()"
              />
              <mat-icon
                matSuffix
                [style.cursor]="searchQuery ? 'pointer' : 'default'"
                (click)="clearSearch()"
                >{{ searchQuery ? "close" : "search" }}</mat-icon
              >
            </mat-form-field>

            <!-- Feedback Type Filter -->
            <mat-form-field appearance="outline" class="type-field">
              <mat-label>Feedback Type</mat-label>
              <mat-select formControlName="categories" multiple>
                @for (type of feedbackTypes; track type.value) {
                  <mat-option [value]="type.value">
                    {{ type.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Start Date Filter -->
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>Start Date</mat-label>
              <input
                matInput
                [matDatepicker]="startPicker"
                formControlName="startDate"
                (dateChange)="onStartDateChange()"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="startPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>

            <!-- End Date Filter -->
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>End Date</mat-label>
              <input
                matInput
                [matDatepicker]="endPicker"
                [min]="minEndDate"
                formControlName="endDate"
                (dateChange)="onEndDateChange()"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="endPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>

            <!-- Reset Button -->
            <button
              mat-stroked-button
              type="button"
              (click)="resetFilters()"
              class="reset-filters-btn"
              [disabled]="!hasFiltersChanged()"
            >
              <mat-icon>replay</mat-icon>
              Reset
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Loading user feedback...</span>
      </div>
    }

    <!-- Error State -->
    @if (error && !isLoading) {
      <app-error-state
        [message]="error"
        (retry)="loadFeedback()"
      ></app-error-state>
    }

    <!-- Table Section -->
    @if (!isLoading && !error) {
      <mat-card class="table-card no-padding">
        <mat-card-content>
          <div class="table-container">
            <table
              mat-table
              [dataSource]="dataSource"
              matSort
              [matSortActive]="sortBy"
              [matSortDirection]="sortDirection"
              matSortDisableClear
              (matSortChange)="onSortChange($event)"
              class="feedback-table"
            >
              <!-- Date Submitted Column -->
              <ng-container matColumnDef="dateSubmitted">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Date Submitted
                </th>
                <td mat-cell *matCellDef="let feedback">
                  <span class="timestamp">{{
                    formatTimestamp(feedback.createdAt)
                  }}</span>
                </td>
              </ng-container>

              <!-- Feedback Type Column -->
              <ng-container matColumnDef="category">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Feedback Type
                </th>
                <td mat-cell *matCellDef="let feedback">
                  <span class="feedback-type">{{ feedback.category }}</span>
                </td>
              </ng-container>

              <!-- User Name Column -->
              <ng-container matColumnDef="userName">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
                <td mat-cell *matCellDef="let feedback">
                  @if (feedback.submittedByEmployeeId) {
                    <a
                      class="cell-link"
                      [routerLink]="[
                        '/resources',
                        feedback.submittedByEmployeeId,
                      ]"
                      >{{ feedback.submittedByName || "-" }}</a
                    >
                  } @else {
                    <span class="user">{{
                      feedback.submittedByName || "-"
                    }}</span>
                  }
                </td>
              </ng-container>

              <!-- User Email Column -->
              <ng-container matColumnDef="userEmail">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>Email</th>
                <td mat-cell *matCellDef="let feedback">
                  <span class="user">{{
                    feedback.submittedByEmail || "-"
                  }}</span>
                </td>
              </ng-container>

              <!-- Message Column -->
              <ng-container matColumnDef="message">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Message
                </th>
                <td mat-cell *matCellDef="let feedback" class="message-cell">
                  <span
                    class="message-text"
                    #messageText
                    [matTooltip]="feedback.message"
                    [matTooltipDisabled]="true"
                    #tooltip="matTooltip"
                    (mouseenter)="updateTooltipDisabled(messageText, tooltip)"
                    matTooltipClass="app-tooltip"
                    matTooltipPosition="above"
                  >
                    {{ feedback.message || "-" }}
                  </span>
                </td>
              </ng-container>

              <!-- Page URL Column -->
              <ng-container matColumnDef="pageUrl">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Page URL
                </th>
                <td mat-cell *matCellDef="let feedback">
                  @if (feedback.pageUrl) {
                    <a class="cell-link" [routerLink]="feedback.pageUrl">{{
                      feedback.pageUrl
                    }}</a>
                  } @else {
                    <span>-</span>
                  }
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>

              <!-- Empty State -->
              <tr class="mat-row" *matNoDataRow>
                <td
                  class="mat-cell no-data-cell"
                  [attr.colspan]="displayedColumns.length"
                >
                  <div class="empty-state">
                    <mat-icon>search_off</mat-icon>
                    <span>No user feedback found matching your filters</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <mat-paginator
            [length]="totalCount"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            (page)="onPageChange($event)"
            showFirstLastButtons
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    }
  </div>
</div>
